import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import galleryData from "./galleryData";
import "./PhotoGallery.css";
import { trackEvent, useTrackElementVisibility } from "./utils/analytics";

// Define the PortaledLightbox component
const PortaledLightbox = ({
  photo,
  currentImageSrc,
  onClose,
  onNavigate,
  onClickOutside,
  contentRef,
  isMobile,
}) => {
  const [navDirection, setNavDirection] = useState(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Move useRef outside of conditional logic
  const handleTouchStart = useRef({ x: 0, y: 0 });
  const handleTouchEnd = useRef({ x: 0, y: 0 });

  // Move useEffect outside of conditional logic
  useEffect(() => {
    if (photo) {
      setIsImageLoaded(false);
    }
  }, [photo?.id]);

  if (!photo) return null;

  const handleNavigation = (direction) => {
    setNavDirection(direction);
    onNavigate(direction);
  };

  const onTouchStart = (e) => {
    handleTouchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const onTouchMove = (e) => {
    handleTouchEnd.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const onTouchEnd = () => {
    const diffX = handleTouchStart.current.x - handleTouchEnd.current.x;
    const diffY = handleTouchStart.current.y - handleTouchEnd.current.y;

    // Only trigger if the horizontal swipe is significant and more than vertical movement
    if (Math.abs(diffX) > 50 && Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX > 0) {
        // Swiped left - go next
        handleNavigation("next");
      } else {
        // Swiped right - go prev
        handleNavigation("prev");
      }
    }
  };

  return ReactDOM.createPortal(
    // This motion.div is for the overall open/close of the lightbox.
    <motion.div
      className="lightbox-shell"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.5, // Keep open duration as is or adjust if needed
        ease: "easeInOut", // Keep open ease as is
        // Specify different properties for exit transition
        opacity: { duration: 0.6, ease: "easeOut" },
      }}
      onClick={onClickOutside}
    >
      {/* Host for the swappable/fading background image layers */}
      <div className="lightbox-background-layers-host">
        <AnimatePresence mode="sync">
          <motion.div
            key={currentImageSrc || "default-bg-layer"}
            className="lightbox-image-background-layer"
            style={{ "--lightbox-bg-image": `url(${currentImageSrc})` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.5, // Keep open duration
              ease: "easeInOut", // Keep open ease
              // Specify different properties for exit transition
              opacity: { duration: 0.6, ease: "easeOut" },
            }}
          />
        </AnimatePresence>
      </div>

      {/* Close button - moved outside content area to always be at top-right of viewport */}
      <button
        className="lightbox-close"
        data-interactive="true"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close lightbox"
      >
        ×
      </button>

      {/* Interactive content area */}
      <div
        className="lightbox-interactive-area"
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchMove={isMobile ? onTouchMove : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
      >
        <button
          className="lightbox-nav lightbox-prev"
          data-interactive="true"
          onClick={(e) => {
            e.stopPropagation();
            handleNavigation("prev");
          }}
          aria-label="Previous image"
        >
          ‹
        </button>

        <motion.div
          className="lightbox-content"
          ref={contentRef}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{
            // Default transition for initial and animate
            default: { duration: 0.4, ease: "easeInOut" },
            // Specific transition for exit
            scale: { duration: 0.5, ease: "easeOut" },
            opacity: { duration: 0.5, ease: "easeOut" },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <AnimatePresence mode="wait" custom={navDirection}>
            <motion.img
              key={photo.id} // Ensure this key is unique to the photo
              src={photo.src}
              alt={photo.alt || "Gallery image"}
              custom={navDirection}
              initial={{ opacity: 0 }} // Start from transparent
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }} // Coordinated timing
              onLoad={() => setIsImageLoaded(true)}
              style={{ visibility: isImageLoaded ? "visible" : "hidden" }}
            />
            {!isImageLoaded && (
              <div className="lightbox-loading-indicator">
                <div className="spinner"></div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>

        <button
          className="lightbox-nav lightbox-next"
          data-interactive="true"
          onClick={(e) => {
            e.stopPropagation();
            handleNavigation("next");
          }}
          aria-label="Next image"
        >
          ›
        </button>
      </div>
    </motion.div>,
    document.body
  );
};

const PhotoGallery = () => {
  // Define sections with useMemo to avoid recreating on every render
  const sections = useMemo(() => {
    const sectionMap = {};
    galleryData.forEach((image) => {
      if (!sectionMap[image.section]) {
        sectionMap[image.section] = [];
      }
      sectionMap[image.section].push(image);
    });
    return sectionMap;
  }, []);

  // Create refs for each section for visibility tracking
  const sectionRefs = useMemo(() => {
    const refs = {};
    Object.keys(sections).forEach((section) => {
      refs[section] = React.createRef();
    });
    return refs;
  }, [sections]);

  // Set up visibility tracking for each section
  useEffect(() => {
    // Create observers for each section
    Object.keys(sectionRefs).forEach((section) => {
      const ref = sectionRefs[section];
      if (ref.current) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                // Track when section becomes visible
                trackEvent("Gallery Sections", "Section Viewed", section);
                // Disconnect after first view
                observer.disconnect();
              }
            });
          },
          { threshold: 0.3, rootMargin: "0px" }
        );

        observer.observe(ref.current);

        return () => {
          observer.disconnect();
        };
      }
    });
  }, [sectionRefs]);

  const sectionTitleConfig = useMemo(
    () => ({
      Storytelling: {
        text: "STORYTELLING",
        mobileText: "STORYTELLING",
      },
      Portraits: {
        text: "PORTRAITS",
        mobileText: "PORTRAITS",
      },
      Performance: {
        text: "PERFORMANCE",
        mobileText: "PERFORMANCE",
      },
      "Mixed Media": {
        text: "MIXED MEDIA",
        mobileText: "MIXED\nMEDIA",
      },
    }),
    []
  );

  const [activeSection, setActiveSection] = useState("landing");
  const [animatedIn, setAnimatedIn] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState("");
  const lightboxRef = useRef(null);
  // Add state to track if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Function to check if we're on mobile
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  // Check mobile on mount and resize
  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [checkMobile]);

  // Add refs for scroll animations
  const scrollItemsRef = useRef([]);

  // Set up intersection observer on mount
  useEffect(() => {
    // Initialize intersection observer for scroll animations
    const observerOptions = {
      root: null, // viewport
      rootMargin: "0px",
      threshold: 0.15, // 15% of the item must be visible
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add visible class when element comes into view
          entry.target.classList.add("visible");
          // Stop observing after animation
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    // Select all gallery items and section headers to observe
    const itemsToAnimate = document.querySelectorAll(
      ".gallery-item, .section-header, .image-grid"
    );

    // Add animate-on-scroll class and observe each element
    itemsToAnimate.forEach((item) => {
      item.classList.add("animate-on-scroll");
      observer.observe(item);
    });

    // Cleanup observer on component unmount
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setAnimatedIn(true);

    // Handle initial section selection from URL hash if present
    const initialHash = window.location.hash.replace("#", "");
    if (initialHash) {
      setActiveSection(initialHash);
      // Delay scrolling slightly to ensure the page is fully rendered
      setTimeout(() => {
        document
          .getElementById(initialHash)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }

    // We can remove the hashchange listener since we're no longer using hash-based navigation
  }, []);

  const navigateToSection = (section) => {
    const sectionEl = document.getElementById(`section-${section}`);
    if (sectionEl) {
      sectionEl.scrollIntoView({ behavior: "smooth" });

      // Track section navigation
      trackEvent("Gallery", "Section Navigation", section);
    }
  };

  const openLightbox = (sectionName, index) => {
    setCurrentSection(sectionName);
    setPhotoIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";

    // Track lightbox open event with image info
    const photo = sections[sectionName][index];
    trackEvent(
      "Gallery",
      "Lightbox Open",
      `${sectionName} - ${photo.alt || `Image ${index + 1}`}`
    );
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";

    // Track lightbox close event
    trackEvent("Gallery", "Lightbox Close");
  };

  const handleOutsideClick = (e) => {
    closeLightbox();
  };

  // Use useCallback for navigateImage to avoid unnecessary rerenders
  const navigateImage = useCallback(
    (direction) => {
      const sectionImages = sections[currentSection];
      if (!sectionImages || sectionImages.length === 0) return;

      let nextIndex;
      if (direction === "next") {
        nextIndex = (photoIndex + 1) % sectionImages.length;
      } else {
        nextIndex =
          (photoIndex - 1 + sectionImages.length) % sectionImages.length;
      }
      setPhotoIndex(nextIndex);

      // Track lightbox navigation
      const photo = sections[currentSection][nextIndex];
      trackEvent(
        "Gallery",
        `Lightbox Navigate ${direction}`,
        `${currentSection} - ${photo.alt || `Image ${nextIndex + 1}`}`
      );
    },
    [photoIndex, currentSection, sections]
  );

  // Update the dependency array to include navigateImage
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;
        case "ArrowRight":
          navigateImage("next");
          break;
        case "ArrowLeft":
          navigateImage("prev");
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, photoIndex, currentSection, navigateImage, sections]);

  // Get left and right landing images early so preloading can use them
  const leftImage = galleryData.find((img) => img.id === 1);
  const rightImage = galleryData.find((img) => img.id === 2);

  // Function to preload an image
  const preloadImage = (src) => {
    if (!src) return Promise.reject(new Error("No source provided"));
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  };

  // Preload images for the current section and upcoming sections
  useEffect(() => {
    // Create a map to track which images have been preloaded
    const preloadedImages = new Map();

    // Helper function to preload an image and mark it as preloaded
    const preloadAndTrack = (src) => {
      if (!src || preloadedImages.has(src)) return;
      preloadedImages.set(src, true);
      preloadImage(src).catch(() => {
        // Silently handle errors, we don't want to break the UI
      });
    };

    // Immediately preload landing images
    if (leftImage?.src) preloadAndTrack(leftImage.src);
    if (rightImage?.src) preloadAndTrack(rightImage.src);

    // Function to load images for a section
    const preloadSectionImages = (sectionName, priority = false) => {
      if (!sections[sectionName]) return;

      const images = sections[sectionName];

      // For high priority (visible sections), load immediately
      if (priority) {
        images.forEach((image) => {
          preloadAndTrack(image.src);
        });
        return;
      }

      // For lower priority, queue with requestIdleCallback or setTimeout
      const queuePreload = (cb) => {
        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(cb, { timeout: 2000 });
        } else {
          setTimeout(cb, 200);
        }
      };

      // Queue preloading of lower priority images
      queuePreload(() => {
        images.forEach((image) => {
          preloadAndTrack(image.src);
        });
      });
    };

    // Start by preloading the active section with high priority
    if (activeSection !== "landing") {
      const activeSectionName = Object.keys(sections).find(
        (section) =>
          section.toLowerCase().replace(/\s+/g, "-") === activeSection
      );
      if (activeSectionName) {
        preloadSectionImages(activeSectionName, true);
      }
    }

    // Then queue preloading of other sections
    queueMicrotask(() => {
      Object.keys(sections).forEach((sectionName) => {
        const sectionId = sectionName.toLowerCase().replace(/\s+/g, "-");
        if (sectionId !== activeSection) {
          preloadSectionImages(sectionName, false);
        }
      });
    });
  }, [sections, activeSection, leftImage, rightImage]);

  // Inside your main PhotoGallery component, replace the lazy loading useEffect:
  useEffect(() => {
    // Process all images that have 'data-src' on first render
    const processImages = () => {
      const images = document.querySelectorAll("img.lazy-image[data-src]");

      images.forEach((img) => {
        const src = img.getAttribute("data-src");
        if (!src) return;

        // Set src immediately
        img.src = src;
        img.removeAttribute("data-src");

        // When the image loads, add the needed classes
        img.onload = () => {
          img.classList.add("loaded");
          const galleryItem = img.closest(".gallery-item");
          if (galleryItem) galleryItem.classList.add("image-loaded");
        };

        img.onerror = () => {
          img.classList.add("error");
        };
      });
    };

    // Process images after a short delay to let the component render
    const timer = setTimeout(processImages, 50);
    return () => clearTimeout(timer);
  }, [sections]);

  // Keep this effect for lightbox image preloading
  useEffect(() => {
    if (lightboxOpen && currentSection && sections[currentSection]) {
      const sectionImages = sections[currentSection];
      if (sectionImages && sectionImages.length > 1) {
        const currentImage = sections[currentSection][photoIndex];
        if (currentImage) {
          // Ensure current image data exists
          const nextPhotoIndex = (photoIndex + 1) % sectionImages.length;
          const prevPhotoIndex =
            (photoIndex - 1 + sectionImages.length) % sectionImages.length;

          if (sections[currentSection][nextPhotoIndex]) {
            preloadImage(sections[currentSection][nextPhotoIndex].src);
          }
          if (sections[currentSection][prevPhotoIndex]) {
            preloadImage(sections[currentSection][prevPhotoIndex].src);
          }
        }
      }
    }
  }, [lightboxOpen, photoIndex, currentSection, sections]);

  const currentPhotoData =
    lightboxOpen &&
    currentSection &&
    sections[currentSection] &&
    sections[currentSection][photoIndex]
      ? sections[currentSection][photoIndex]
      : null;

  const currentImageSrc = currentPhotoData ? currentPhotoData.src : null;

  return (
    <div className="photo-gallery-container">
      <div
        className={`landing-screen ${animatedIn ? "animated-in" : ""}`}
        id="landing"
      >
        <div className="landing-layout">
          {/* Only render image containers if not on mobile */}
          {!isMobile && (
            <div className="landing-image-container left">
              <div className="landing-image-wrapper">
                <img
                  src={leftImage?.src}
                  alt={leftImage?.alt}
                  className="landing-image"
                />
              </div>
            </div>
          )}

          <div className="landing-center">
            <h1 className="landing-title">My Gallery</h1>
            <div className="landing-nav">
              {Object.keys(sections).map((section, index) => (
                <button
                  key={section}
                  className="nav-button"
                  data-interactive="true"
                  onClick={() =>
                    navigateToSection(
                      section.toLowerCase().replace(/\s+/g, "-")
                    )
                  }
                >
                  {section}
                </button>
              ))}
            </div>
          </div>

          {/* Only render image containers if not on mobile */}
          {!isMobile && (
            <div className="landing-image-container right">
              <div className="landing-image-wrapper">
                <img
                  src={rightImage?.src}
                  alt={rightImage?.alt}
                  className="landing-image"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {Object.keys(sections).map((sectionName, sectionIndex) => (
        <section
          key={sectionName}
          id={sectionName.toLowerCase().replace(/\s+/g, "-")}
          className={`gallery-section section-${sectionName
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
          ref={sectionRefs[sectionName]}
        >
          <div className="section-header">
            {sections[sectionName] && (
              <h2 className="section-title">
                {isMobile
                  ? sectionTitleConfig[sectionName]?.mobileText || sectionName
                  : sectionTitleConfig[sectionName]?.text || sectionName}
              </h2>
            )}
          </div>

          <div className="image-grid">
            {sections[sectionName].map((image, index) => (
              <div
                key={image.id}
                className={`gallery-item ${image.sizeClass || ""}`}
                data-interactive="true"
                onClick={() => openLightbox(sectionName, index)}
              >
                <img
                  data-src={image.src}
                  alt={image.alt || "Gallery image"}
                  className="lazy-image"
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* Lightbox Component */}
      <AnimatePresence>
        {lightboxOpen && currentPhotoData && (
          <PortaledLightbox
            key="lightbox"
            photo={currentPhotoData}
            currentImageSrc={currentImageSrc}
            onClose={closeLightbox}
            onNavigate={navigateImage}
            onClickOutside={handleOutsideClick}
            contentRef={lightboxRef}
            isMobile={isMobile}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoGallery;
